<template>
	<div class="reservations-timeline" :style="rootStyle">

		<div class="timeline-topbar">
			<div class="room-tabs" v-if="rooms && rooms.length">
				<div
					class="room-tab"
					v-for="room in rooms"
					:key="room.room_id"
					:class="{active: selectedRoom && selectedRoom.room_id === room.room_id}"
					@click="selectRoom(room)">
					{{ room.name ? room.name : getString('no_name') }}
				</div>
			</div>
			<div class="topbar-actions">
				<AppLoader v-if="loadingData" :size="18" />
				<ReservationsViewToggle />
			</div>
		</div>

		<div class="timeline-empty" v-if="!rooms || !rooms.length">
			<p>{{ getString('no_tables') }}</p>
		</div>

		<div class="timeline-scroll" v-else>
			<div class="timeline-inner" :style="{width: innerWidth}">

				<div class="axis-row">
					<div class="corner"></div>
					<div class="axis-track" :style="{width: trackWidthPx}">
						<div class="segment-gap" v-for="(g, gi) in axis.gaps" :key="'agap' + gi" :style="{left: g.leftPx + 'px', width: g.widthPx + 'px'}"></div>
						<div
							class="axis-segment"
							v-for="(seg, si) in axis.segments"
							:key="'seg' + si"
							:style="{left: seg.leftPx + 'px', width: seg.widthPx + 'px'}">
							<div class="segment-name" v-if="seg.label">{{ seg.label }}</div>
							<div
								class="axis-tick"
								v-for="tick in seg.ticks"
								:key="tick.minutes"
								:class="{hour: tick.isHour}"
								:style="{left: tick.leftInSeg + 'px'}">
								<span class="tick-label">{{ tick.text }}</span>
							</div>
						</div>
					</div>
				</div>

				<div class="timeline-row unassigned" v-if="unassignedData.blocks.length" :style="rowHeightStyle(unassignedData.laneCount)">
					<div class="row-label">
						<div class="table-name">{{ getString('unassigned') }}</div>
					</div>
					<div class="row-track" :style="{width: trackWidthPx}">
						<div class="segment-bg" v-for="(seg, si) in axis.segments" :key="'ubg' + si" :style="{left: seg.leftPx + 'px', width: seg.widthPx + 'px'}"></div>
						<div class="segment-gap" v-for="(g, gi) in axis.gaps" :key="'ugap' + gi" :style="{left: g.leftPx + 'px', width: g.widthPx + 'px'}"></div>
						<div class="past-shade" v-if="isTodayView && pastWidthPx > 0" :style="{width: pastWidthPx + 'px'}"></div>
						<div class="now-line" v-if="showNowLine" :style="{left: nowX + 'px'}"></div>
						<div
							class="res-block"
							v-for="block in unassignedData.blocks"
							:key="block.reservation.reservation_id"
							:class="{dimmed: isDimmed(block.reservation)}"
							:style="blockStyle(block)"
							:title="blockTitle(block.reservation)"
							@click="openAssignTable(block.reservation)">
							<div class="block-name">{{ block.reservation.name }}</div>
							<div class="block-meta">
								<span class="block-seats">{{ 'x' + block.reservation.people }}</span>
								<span class="block-warning" v-if="isLate(block.reservation)" v-html="svgStore.get('iconWarning')"></span>
							</div>
						</div>
					</div>
				</div>

				<div class="group-header" v-if="selectedRoom">
					<div class="group-header-inner">{{ selectedRoom.name ? selectedRoom.name : getString('no_name') }}</div>
					<div class="now-line group" v-if="showNowLine" :style="{left: 'calc(var(--label-width) + ' + nowX + 'px)'}">
						<span class="now-label">{{ nowTimeLabel }}</span>
					</div>
				</div>

				<div
					class="timeline-row"
					v-for="row in tableRows"
					:key="row.table.room_table_id"
					:style="rowHeightStyle(row.laneCount)">
					<div class="row-label">
						<div class="table-name">{{ row.table.table_name }}</div>
						<div class="table-seats" v-if="row.table.seat_number">{{ getString('seats_x', [row.table.seat_number]) }}</div>
					</div>
					<div class="row-track clickable" :style="{width: trackWidthPx}" @click="onTrackClick($event, row)">
						<div class="segment-bg" v-for="(seg, si) in axis.segments" :key="'bg' + si" :style="{left: seg.leftPx + 'px', width: seg.widthPx + 'px'}"></div>
						<div class="segment-gap" v-for="(g, gi) in axis.gaps" :key="'gap' + gi" :style="{left: g.leftPx + 'px', width: g.widthPx + 'px'}"></div>
						<div class="past-shade" v-if="isTodayView && pastWidthPx > 0" :style="{width: pastWidthPx + 'px'}"></div>
						<div class="now-line" v-if="showNowLine" :style="{left: nowX + 'px'}"></div>
						<div
							class="res-block"
							v-for="block in row.blocks"
							:key="block.reservation.reservation_id"
							:class="{dimmed: isDimmed(block.reservation)}"
							:style="blockStyle(block)"
							:title="blockTitle(block.reservation)"
							@click.stop="openReservation(block.reservation)">
							<div class="block-name">{{ block.reservation.name }}</div>
							<div class="block-meta">
								<span class="block-seats">{{ 'x' + block.reservation.people }}</span>
								<span class="block-warning" v-if="isLate(block.reservation)" v-html="svgStore.get('iconWarning')"></span>
							</div>
						</div>
					</div>
				</div>

				<div class="timeline-empty inline" v-if="selectedRoom && !tableRows.length && !unassignedData.blocks.length">
					<p>{{ getString('no_tables') }}</p>
				</div>

			</div>
		</div>

	</div>
</template>

<script>
import {useRoomsStore, useRoomsOrdersStore, useExperiencesStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import AppLoader from "@/shared/components/AppLoader.vue";
import ReservationsViewToggle from "@/console-new/components/ops/tables/ReservationsViewToggle.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {doubleFigure, isTodayServerDate} from "@/shared/helpers/dates";
import {isTextColorDark} from "@/shared/helpers/ui";
import API from "@/shared/helpers/API";

// Pastel palette for reservation blocks (assigned deterministically by reservation id).
const BLOCK_PALETTE = [
	"#B79CED", "#E88AA6", "#8CC79E", "#EBB07A",
	"#85A6D6", "#D19AE0", "#7FD1C4", "#E0A0A0"
];

// Finished reservations are shown in a neutral grey instead of their palette color.
const FINISHED_COLOR = "#AEB2BA";

export default {
	name: "ReservationsTimeline",
	components: {AppLoader, ReservationsViewToggle},
	mixins: [WindowWidth],
	setup() {
		const languageStore = useLanguageStore();
		const roomsStore = useRoomsStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const experiencesStore = useExperiencesStore();
		const userStore = useUserStore();
		const svgStore = useSvgStore();
		const notificationStore = useNotificationStore();
		return {
			getString: languageStore.getString,
			languageStore,
			roomsStore,
			roomsOrdersStore,
			experiencesStore,
			userStore,
			svgStore,
			notificationStore
		};
	},
	data() {
		return {
			loadingData: false,
			palette: BLOCK_PALETTE,
			slotMinutes: 30,
			LANE_HEIGHT: 52,
			LANE_GAP: 6,
			ROW_PAD: 8,
			MIN_BLOCK_WIDTH: 70,
			now: new Date(),
			nowInterval: null,
		};
	},
	computed: {
		rooms() {
			return this.roomsStore.roomsTables || [];
		},
		selectedRoom() {
			const rooms = this.rooms;
			if(!rooms || !rooms.length) { return null; }
			const id = this.roomsStore.activeRoomId || rooms[0].room_id;
			return rooms.find(room => room.room_id === id) || rooms[0];
		},
		reservations() {
			return this.roomsOrdersStore.reservationsCurrDate || [];
		},
		slotWidth() {
			if(this.windowWidth < 640) { return 48; }
			if(this.windowWidth < 1024) { return 58; }
			return 68;
		},
		segmentGap() {
			return this.windowWidth < 640 ? 24 : 40;
		},
		labelWidth() {
			return this.windowWidth < 640 ? 96 : 132;
		},
		rootStyle() {
			return {
				"--slot-width": this.slotWidth + "px",
				"--label-width": this.labelWidth + "px"
			};
		},
		reservationsRange() {
			const res = this.reservations;
			if(!res.length) { return {start: 11 * 60, end: 24 * 60}; }
			let minStart = Infinity;
			let maxEnd = -Infinity;
			res.forEach(r => {
				const s = this.minutesOfDay(r.fullDate);
				minStart = Math.min(minStart, s);
				maxEnd = Math.max(maxEnd, s + r.duration);
			});
			let start = Math.floor(minStart / 60) * 60;
			let end = Math.ceil(maxEnd / 60) * 60;
			if(end - start < 240) { end = start + 240; }
			return {start, end};
		},
		// Timeslots narrowed to the active experience selection. The backend returns timeslots
		// unfiltered, so the axis/segments would otherwise keep showing a filtered-out experience.
		// Mirrors the server-side reservation filter semantics: an absent or empty selection means
		// "all experiences" (matching how blocks are fetched), so axis and blocks stay consistent.
		filteredTimeslots() {
			const slots = this.roomsOrdersStore.reservationsTimeslots || [];
			const selected = this.roomsOrdersStore.reservationsFilters.experiences;
			if(!Array.isArray(selected) || !selected.length) { return slots; }
			const set = selected.map(id => Number(id));
			return slots.filter(t => t.experience_id == null || set.includes(Number(t.experience_id)));
		},
		// Builds the segmented time axis from the day's service windows (timeslots).
		// Each contiguous service window becomes a labeled segment; segments are laid out
		// left-to-right separated by a fixed visual gap (non-service hours are not drawn to scale).
		axis() {
			const slotMin = this.slotMinutes;
			const slotW = this.slotWidth;
			const gap = this.segmentGap;

			let windows = this.filteredTimeslots
				.map(t => ({name: t.name, startMin: this.timeStrToMin(t.time_start), endMin: this.timeStrToMin(t.time_end)}))
				.filter(w => Number.isFinite(w.startMin) && Number.isFinite(w.endMin) && w.endMin > w.startMin)
				.sort((a, b) => a.startMin - b.startMin);

			let segments = this.mergeSegments(
				windows.map(w => ({names: [w.name], startMin: w.startMin, endMin: w.endMin}))
			);

			if(!segments.length) {
				const r = this.reservationsRange;
				segments.push({names: [], startMin: r.start, endMin: r.end});
			}

			// Guarantee every reservation is visible by extending the nearest segment to cover it.
			this.reservations.forEach(res => {
				const s = this.minutesOfDay(res.fullDate);
				const e = s + res.duration;
				let seg = segments.find(m => s >= m.startMin && s <= m.endMin);
				if(!seg) {
					let best = null;
					segments.forEach(m => {
						const dist = s < m.startMin ? (m.startMin - s) : (s > m.endMin ? (s - m.endMin) : 0);
						if(best === null || dist < best.dist) { best = {seg: m, dist}; }
					});
					seg = best.seg;
				}
				seg.startMin = Math.min(seg.startMin, s);
				seg.endMin = Math.max(seg.endMin, e);
			});

			// The extension above mutates segment ranges in place and can push one segment past the
			// next (e.g. LUNCH extended past DINNER's start), producing overlapping ranges and
			// duplicate axis columns. Re-merge so newly-overlapping/touching segments collapse into
			// one continuous segment; genuine non-service gaps (still-disjoint segments) are kept.
			segments = this.mergeSegments(segments);

			let offset = 0;
			segments.forEach((seg) => {
				seg.slotStart = Math.floor(seg.startMin / slotMin) * slotMin;
				seg.slotEnd = Math.ceil(seg.endMin / slotMin) * slotMin;
				if(seg.slotEnd <= seg.slotStart) { seg.slotEnd = seg.slotStart + slotMin; }
				seg.slotCount = (seg.slotEnd - seg.slotStart) / slotMin;
				seg.widthPx = seg.slotCount * slotW;
				seg.leftPx = offset;
				seg.label = seg.names.filter(Boolean).join(" / ");
				seg.ticks = [];
				for(let m = seg.slotStart; m <= seg.slotEnd; m += slotMin) {
					// Skip the closing boundary tick: on gapped segments its label would spill
					// into the gap band, and on the last segment it overflows past the right edge.
					if(m === seg.slotEnd) { continue; }
					seg.ticks.push({
						minutes: m,
						leftInSeg: ((m - seg.slotStart) / slotMin) * slotW,
						text: this.formatMinutes(m),
						isHour: m % 60 === 0
					});
				}
				offset += seg.widthPx + gap;
			});

			let gaps = [];
			for(let i = 1; i < segments.length; i++) {
				const prev = segments[i - 1];
				gaps.push({leftPx: prev.leftPx + prev.widthPx, widthPx: gap});
			}

			return {segments, gaps, totalWidth: Math.max(0, offset - gap)};
		},
		trackWidthPx() {
			return this.axis.totalWidth + "px";
		},
		innerWidth() {
			return "calc(var(--label-width) + " + this.axis.totalWidth + "px)";
		},
		reservationsByTable() {
			const map = {};
			this.reservations.forEach(r => {
				(r.tables || []).forEach(tableId => {
					const key = Number(tableId);
					if(!map[key]) { map[key] = []; }
					map[key].push(r);
				});
			});
			return map;
		},
		tableRows() {
			if(!this.selectedRoom || !this.selectedRoom.tables) { return []; }
			const tables = [...this.selectedRoom.tables].sort((a, b) => a.order_number - b.order_number);
			return tables.map(table => {
				const list = this.reservationsByTable[table.room_table_id] || [];
				const {blocks, laneCount} = this.buildBlocks(list);
				return {table, blocks, laneCount};
			});
		},
		unassignedData() {
			const list = this.reservations.filter(r => !r.tables || r.tables.length === 0);
			return this.buildBlocks(list);
		},
		// The "now" cues (past shading + current-time line) only make sense on today's timeline.
		isTodayView() {
			return isTodayServerDate(this.roomsOrdersStore.reservationsSelectedDate, false);
		},
		nowMinutes() {
			return this.minutesOfDay(this.now);
		},
		// Track-local x (px) of the current moment, mapped through the same piecewise-segment math
		// as blockStyle(). null when not today or there is no axis; clamps to segment edges in gaps.
		nowX() {
			if(!this.isTodayView) { return null; }
			const segs = this.axis.segments;
			if(!segs.length) { return null; }
			const m = this.nowMinutes;
			if(m <= segs[0].slotStart) { return 0; }
			const last = segs[segs.length - 1];
			if(m >= last.slotEnd) { return this.axis.totalWidth; }
			const seg = this.segmentForMinute(m);
			const clamped = Math.min(Math.max(m, seg.slotStart), seg.slotEnd);
			return seg.leftPx + ((clamped - seg.slotStart) / this.slotMinutes) * this.slotWidth;
		},
		pastWidthPx() {
			return this.nowX == null ? 0 : this.nowX;
		},
		showNowLine() {
			return this.nowX != null && this.nowX > 0 && this.nowX < this.axis.totalWidth;
		},
		nowTimeLabel() {
			return this.formatMinutes(this.nowMinutes);
		}
	},
	methods: {
		minutesOfDay(date) {
			return (date.getHours() * 60) + date.getMinutes();
		},
		timeStrToMin(timeStr) {
			if(!timeStr) { return NaN; }
			const parts = timeStr.split(":");
			return (parseInt(parts[0], 10) * 60) + parseInt(parts[1], 10);
		},
		formatMinutes(m) {
			const h = Math.floor(m / 60) % 24;
			const mm = m % 60;
			return doubleFigure(h) + ":" + doubleFigure(mm);
		},
		// Merges service-window segments whose [startMin, endMin] ranges overlap or touch into
		// single contiguous segments. Sorts internally so it is safe to call both on the initial
		// (pre-sorted) windows and again after the reservation-extension pass may have pushed one
		// segment's range into the next. Names are unioned in order with de-duplication.
		mergeSegments(segs) {
			const sorted = [...segs].sort((a, b) => a.startMin - b.startMin);
			const merged = [];
			sorted.forEach(seg => {
				const last = merged[merged.length - 1];
				if(last && seg.startMin <= last.endMin) {
					last.endMin = Math.max(last.endMin, seg.endMin);
					seg.names.forEach(n => {
						if(!last.names.includes(n)) { last.names.push(n); }
					});
				} else {
					merged.push({names: [...seg.names], startMin: seg.startMin, endMin: seg.endMin});
				}
			});
			return merged;
		},
		buildBlocks(list) {
			const blocks = list.map(r => {
				const startMin = this.minutesOfDay(r.fullDate);
				return {reservation: r, startMin, endMin: startMin + r.duration, lane: 0};
			}).sort((a, b) => (a.startMin - b.startMin) || (a.endMin - b.endMin));

			const laneEnds = [];
			blocks.forEach(block => {
				let placed = false;
				for(let i = 0; i < laneEnds.length; i++) {
					if(block.startMin >= laneEnds[i]) {
						block.lane = i;
						laneEnds[i] = block.endMin;
						placed = true;
						break;
					}
				}
				if(!placed) {
					block.lane = laneEnds.length;
					laneEnds.push(block.endMin);
				}
			});
			return {blocks, laneCount: Math.max(1, laneEnds.length)};
		},
		segmentForMinute(min) {
			const segs = this.axis.segments;
			let seg = segs.find(s => min >= s.slotStart && min < s.slotEnd);
			if(seg) { return seg; }
			let best = null;
			segs.forEach(s => {
				const dist = min < s.slotStart ? (s.slotStart - min) : (min > s.slotEnd ? (min - s.slotEnd) : 0);
				if(best === null || dist < best.dist) { best = {s, dist}; }
			});
			return best ? best.s : segs[0];
		},
		rowHeightStyle(laneCount) {
			const height = (laneCount * (this.LANE_HEIGHT + this.LANE_GAP)) - this.LANE_GAP + (this.ROW_PAD * 2);
			return {height: height + "px"};
		},
		blockStyle(block) {
			const seg = this.segmentForMinute(block.startMin);
			const startClamped = Math.max(block.startMin, seg.slotStart);
			const endClamped = Math.min(block.endMin, seg.slotEnd);
			const left = seg.leftPx + ((startClamped - seg.slotStart) / this.slotMinutes) * this.slotWidth;
			let width = ((endClamped - startClamped) / this.slotMinutes) * this.slotWidth;
			width = Math.max(width, this.MIN_BLOCK_WIDTH);
			const segRight = seg.leftPx + seg.widthPx;
			if((left + width) > segRight && (segRight - left) >= this.MIN_BLOCK_WIDTH) {
				width = segRight - left;
			}
			const top = this.ROW_PAD + (block.lane * (this.LANE_HEIGHT + this.LANE_GAP));
			const bg = block.reservation.status === "finished"
				? FINISHED_COLOR
				: this.colorFor(block.reservation.reservation_id);
			return {
				left: left + "px",
				width: width + "px",
				top: top + "px",
				height: this.LANE_HEIGHT + "px",
				backgroundColor: bg,
				color: isTextColorDark(bg) ? "#1c1c2e" : "#ffffff"
			};
		},
		colorFor(id) {
			const idx = Math.abs(Number(id)) % this.palette.length;
			return this.palette[idx];
		},
		isDimmed(reservation) {
			return ["canceled", "noshow"].includes(reservation.status);
		},
		isLate(reservation) {
			return reservation?.status && !['arrived', 'finished', 'noshow', 'canceled'].includes(reservation.status) && reservation.fullDate < new Date();
		},
		blockTitle(reservation) {
			let parts = [reservation.name, reservation.timeDisplay, this.getString('seats_x', [reservation.people])];
			const exp = this.getExperienceName(reservation);
			if(exp) { parts.push(exp); }
			return parts.join(" · ");
		},
		getExperienceName(reservation) {
			if(!reservation || !reservation.experience_id) { return ''; }
			const exp = this.experiencesStore.getExperienceById(Number(reservation.experience_id));
			return exp ? exp.name : '';
		},
		selectRoom(room) {
			this.roomsStore.setActiveRoom(room.room_id);
		},
		openReservation(reservation) {
			this.roomsOrdersStore.manageReservationId = reservation.reservation_id;
		},
		openAssignTable(reservation) {
			if(!reservation) { return; }
			// Unassigned reservations skip the "manage" sheet and go straight to table assignment:
			// open the edit sheet, then trigger the tables selector once it has mounted.
			this.roomsOrdersStore.editReservationId = reservation.reservation_id;
			setTimeout(() => {
				this.roomsOrdersStore.editReservationTables = true;
			}, 200);
		},
		onTrackClick(event, row) {
			const rect = event.currentTarget.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const seg = this.axis.segments.find(s => x >= s.leftPx && x <= (s.leftPx + s.widthPx));
			if(!seg) { return; }
			const slotIndex = Math.floor((x - seg.leftPx) / this.slotWidth);
			let minute = seg.slotStart + (slotIndex * this.slotMinutes);
			minute = Math.min(Math.max(minute, seg.slotStart), seg.slotEnd - this.slotMinutes);
			// Segments can extend past midnight when a reservation runs long. A slot at/after 24:00
			// belongs to the next day, but the preset date is pinned to the selected day, so allowing
			// it would silently create the reservation on the wrong (previous) day.
			if(minute >= 24 * 60) {
				this.notificationStore.showNotification('reservation-after-midnight');
				return;
			}
			const base = this.roomsOrdersStore.reservationsSelectedDate || new Date();
			const presetDate = new Date(base);
			presetDate.setHours(0, 0, 0, 0);
			const presetTime = new Date(base);
			presetTime.setHours(Math.floor(minute / 60) % 24, minute % 60, 0, 0);
			this.roomsOrdersStore.newReservationPreset = {date: presetDate, time: presetTime, experienceId: this.experienceForMinute(minute)};
			this.roomsOrdersStore.newReservationTableId = row.table.room_table_id;
		},
		experienceForMinute(minute) {
			const slots = this.filteredTimeslots;
			for(const t of slots) {
				const s = this.timeStrToMin(t.time_start);
				const e = this.timeStrToMin(t.time_end);
				if(Number.isFinite(s) && Number.isFinite(e) && minute >= s && minute < e) {
					return t.experience_id != null ? Number(t.experience_id) : null;
				}
			}
			return null;
		},
		loadReservations() {
			if(this.userStore.currBusinessId === null) { return; }
			this.loadingData = true;
			this.roomsOrdersStore.getDayReservations(() => {
				this.loadingData = false;
			});
		},
		handlePrint(newVal) {
			if(!newVal) { return; }
			if(this.reservations.length === 0) {
				this.notificationStore.showNotification('no-reservation-to-print');
				this.roomsOrdersStore.reservationsDayToPrint = null;
				return;
			}
			let filters = {};
			if(Object.entries(this.roomsOrdersStore.reservationsFilters).length > 0) {
				filters = this.roomsOrdersStore.reservationsFilters;
			} else {
				filters = {
					status: this.roomsOrdersStore.standardStatus,
					experiences: this.experiencesStore.hasExperiences ? this.experiencesStore.businessExperiences.map(e => e.experience_id) : []
				};
			}
			API.init().printReservationsDay(this.userStore.currBusinessId, newVal, filters, (response) => {
				if(response.result === 'OK') {
					this.notificationStore.showNotification('print-reservations-success');
				} else {
					this.notificationStore.showNotification('generic-error');
				}
				this.roomsOrdersStore.reservationsDayToPrint = null;
			}, () => {
				this.notificationStore.showNotification('generic-error');
				this.roomsOrdersStore.reservationsDayToPrint = null;
			});
		}
	},
	mounted() {
		this.roomsStore.getBusinessRooms();
		if(this.userStore.hasReservations && !this.experiencesStore.businessExperiences) {
			this.experiencesStore.getBusinessExperiences();
		}
		// Advance the "now" marker every minute so the past shade and current-time line drift right.
		this.nowInterval = setInterval(() => {
			this.now = new Date();
		}, 60000);
	},
	beforeUnmount() {
		clearInterval(this.nowInterval);
	},
	watch: {
		"roomsOrdersStore.reservationsSelectedDate": {
			handler(newVal) {
				// The timeline is a single-day view: blocks are positioned by time-of-day only, so a
				// null ("no date") value — which the API answers with up to 200 reservations across many
				// days — would stack unrelated days on the same rows as if double-booked. Snap the shared
				// date back to today; that re-fires this handler with a concrete day, which then loads.
				if(newVal === null) {
					this.roomsOrdersStore.reservationsSelectedDate = new Date();
					return;
				}
				this.loadReservations();
			},
			immediate: true
		},
		"userStore.currBusinessId": function(newVal, oldVal) {
			if(newVal !== null && newVal !== oldVal) {
				this.loadReservations();
				if(this.userStore.hasReservations && !this.experiencesStore.businessExperiences) {
					this.experiencesStore.getBusinessExperiences();
				}
			}
		},
		"roomsOrdersStore.reservationsDayToPrint": function(newVal, oldVal) {
			if(newVal && newVal !== oldVal) {
				this.handlePrint(newVal);
			}
		}
	}
};
</script>

<style scoped lang="scss">
.reservations-timeline {
	padding: var(--orders-padding);
	display: flex;
	flex-direction: column;
	gap: 0;
	// Match the table (list) view: cap content at 1620px and center, keeping the
	// outer padding as a gutter (box-sizing is border-box, so add the padding back).
	width: 100%;
	max-width: calc(1620px + var(--orders-padding) * 2);
	margin-inline: auto;

	.timeline-topbar {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		flex-wrap: unset;
		gap: 10px;
		background-color: var(--color-bg-primary-2);
		border-radius: 13px 13px 0 0;
		padding: 10px 12px;
		min-height: 55px;

		.room-tabs {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 8px;
			flex: 1;
			min-width: 0;
			overflow-x: auto;
			@include hideScrollbar;

			.room-tab {
				border-radius: 13px;
				border: 1px solid var(--color-primary);
				color: var(--color-primary);
				font-size: 15px;
				padding: 7px 14px;
				cursor: pointer;
				user-select: none;
				white-space: nowrap;
				transition: background-color 0.3s ease, color 0.3s ease;

				&.active {
					background-color: var(--color-active-button);
					color: var(--color-active-button-text);
				}
			}
		}

		.topbar-actions {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-left: auto;
			flex-shrink: 0;
		}
	}

	.timeline-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 120px;
		border-radius: 0 0 13px 13px;
		border: 2px dashed var(--color-primary);

		p {
			font-size: 16px;
			color: var(--color-primary);
		}

		&.inline {
			border: none;
			min-height: 80px;
			width: 100%;
		}
	}

	.timeline-scroll {
		overflow: auto;
		max-height: calc(100vh - 230px);
		border-radius: 0 0 13px 13px;
		background-color: var(--color-bg);
		box-shadow: 0 3px 6px var(--color-box-shadow-2);
		@include smallScrollbar;

		.timeline-inner {
			position: relative;
			min-width: 100%;

			.axis-row {
				display: flex;
				flex-direction: row;
				position: sticky;
				top: 0;
				z-index: 4;
				height: 50px;
				background-color: var(--color-bg);
				box-shadow: inset 0 -1px 0 var(--color-box-shadow-3);

				.corner {
					position: sticky;
					left: 0;
					z-index: 6;
					flex: 0 0 var(--label-width);
					width: var(--label-width);
					background-color: var(--color-bg);
					box-shadow: inset -1px 0 0 var(--color-box-shadow-3);
				}

				.axis-track {
					position: relative;
					flex-shrink: 0;

					.axis-segment {
						position: absolute;
						top: 0;
						height: 50px;

						.segment-name {
							position: absolute;
							top: 5px;
							left: 2px;
							padding-left: 12px;
							font-size: 12px;
							font-weight: 700;
							letter-spacing: 0.6px;
							text-transform: uppercase;
							color: var(--color-primary);
							white-space: nowrap;
						}

						.axis-tick {
							position: absolute;
							top: 27px;
							font-size: 12px;
							color: var(--color-txt-2);
							white-space: nowrap;
							padding-left: 3px;
							box-shadow: inset 1px 0 0 var(--color-box-shadow-3);

							&.hour .tick-label {
								font-weight: 700;
								color: var(--color-txt);
							}
						}
					}
				}
			}

			.segment-gap {
				position: absolute;
				top: 0;
				height: 100%;
				background-color: var(--color-bg-primary-2);
				pointer-events: none;
			}

			.group-header {
				display: flex;
				position: relative;
				background-color: var(--color-bg-primary-1);
				box-shadow: inset 0 -1px 0 var(--color-box-shadow-3);

				.group-header-inner {
					position: sticky;
					left: 0;
					z-index: 2;
					flex: 0 0 var(--label-width);
					width: var(--label-width);
					padding: 8px var(--orders-padding);
					font-size: 16px;
					font-weight: 700;
					color: var(--color-txt);
					background-color: var(--color-bg-primary-1);
					@include truncateString(1);
				}

				// Current-moment marker lives in this band; the vertical line stops here rather
				// than continuing up into the sticky time axis. The room name (higher z-index)
				// stays readable if the marker ever slides underneath it.
				.now-line.group {
					position: absolute;
					top: 0;
					height: 100%;
					width: 2px;
					transform: translateX(-1px);
					background-color: rgba(var(--rgb-primary), 0.45);
					pointer-events: none;
					z-index: 1;

					.now-label {
						position: absolute;
						top: 50%;
						left: 0;
						transform: translate(-50%, -50%);
						padding: 1px 6px;
						border-radius: 6px;
						font-size: 11px;
						font-weight: 700;
						line-height: 1.6;
						color: var(--color-txt-white);
						background-color: var(--color-primary);
						white-space: nowrap;
					}
				}
			}

			.timeline-row {
				display: flex;
				flex-direction: row;
				box-shadow: inset 0 -1px 0 var(--color-box-shadow-3);

				&.unassigned {
					background-color: var(--color-primary-alpha-1, rgba(7, 82, 163, 0.06));

					.row-label {
						background-color: #FC229122;

						.table-name {
							text-overflow: unset;
							white-space: wrap;
						}
					}
				}

				.row-label {
					position: sticky;
					left: 0;
					z-index: 4;
					flex: 0 0 var(--label-width);
					width: var(--label-width);
					display: flex;
					flex-direction: column;
					justify-content: center;
					padding: 0 10px;
					background-color: var(--color-bg);
					box-shadow: inset -1px 0 0 var(--color-box-shadow-3);

					.table-name {
						font-size: 15px;
						font-weight: 700;
						color: var(--color-txt);
						@include truncateString(1);
					}

					.table-seats {
						font-size: 12px;
						color: var(--color-txt-2);
					}
				}

				.row-track {
					position: relative;
					flex-shrink: 0;

					&.clickable {
						cursor: pointer;
					}

					.segment-bg {
						position: absolute;
						top: 0;
						height: 100%;
						background-image: repeating-linear-gradient(to right, var(--color-box-shadow-3) 0, var(--color-box-shadow-3) 1px, transparent 1px, transparent var(--slot-width));
						box-shadow: inset 1px 0 0 var(--color-box-shadow-3), inset -1px 0 0 var(--color-box-shadow-3);
					}

					// Subtle tint over the part of today that has already passed.
					.past-shade {
						position: absolute;
						top: 0;
						left: 0;
						height: 100%;
						background-color: rgba(var(--rgb-txt), 0.12);
						pointer-events: none;
					}

					// Thin current-moment marker, drawn above blocks so it stays visible across them.
					.now-line {
						position: absolute;
						top: 0;
						height: 100%;
						width: 2px;
						transform: translateX(-1px);
						background-color: rgba(var(--rgb-primary), 0.45);
						pointer-events: none;
						z-index: 3;
					}

					.res-block {
						position: absolute;
						z-index: 1;
						border-radius: 10px;
						padding: 6px 8px;
						box-sizing: border-box;
						display: flex;
						flex-direction: column;
						justify-content: center;
						gap: 2px;
						overflow: hidden;
						cursor: pointer;
						transition: transform 0.15s ease;

						&:hover {
							transform: translateY(-1px);
							z-index: 2;
						}

						&.dimmed {
							opacity: 0.5;

							.block-name {
								text-decoration: line-through;
							}
						}

						.block-name {
							font-size: 13px;
							line-height: 16px;
							font-weight: 700;
							white-space: nowrap;
							overflow: hidden;
							text-overflow: ellipsis;
						}

						.block-meta {
							display: flex;
							flex-direction: row;
							align-items: center;
							justify-content: space-between;
							gap: 6px;
							font-size: 12px;
							line-height: 14px;

							// The subtitle uses a <span>, which the global `span { color }` rule paints
							// with the theme text color (white in dark mode). Force it to inherit the
							// block's own contrast color so it stays dark on the pastel in every theme.
							.block-seats {
								color: inherit;
								white-space: nowrap;
							}

							.block-warning {
								display: inline-flex;
								align-items: center;
								justify-content: center;
								width: 14px;
								height: 14px;
								flex-shrink: 0;

								:deep(svg) {
									width: 14px;
									height: 14px;

									.fill { fill: var(--color-danger); }
									.stroke { stroke: var(--color-danger); }
								}
							}
						}
					}
				}
			}
		}
	}

	@media (max-width: 640px) {
		.timeline-scroll {
			max-height: calc(100vh - 260px);
		}
	}
}
</style>
